import analytics, {
  ADD_TEXT,
  DESELECT_OBJECTS,
  MAIN_SELECT,
} from '@repo/analytics';
import { OnboardingState } from '@repo/domain/user';
import IconTextLayer from '@repo/ui-icons/IconTextLayer';
import { themeStyle } from '@repo/ui-kit/themeStyle';

import {
  OnboardingStepConfig,
  OnboardingKeys,
  OnboardingStoreState,
  SurveyInitParams,
  OnboardingVideoState,
  AnchorRefKeys,
} from './types';
import { calcStepsCount } from './utils/calcStepsCount';
import { isValidUserStep } from './utils/isValidUserStep';
import { updateOnboardingState } from './utils/updateOnboardingState';

const noop = () => {};

export const ONBOARDING_USER_SURVERY_KEY = 'htcOnboarding'; // used in editor/userStore
const SHOW_ONBOARDING_VIDEO_POPUP_LOCAL_STORAGE_KEY =
  'htcShowOnboardingVideoPopup';

const ONBOARDING_STEP = `onboarding:step`;

export const BASE_ONBOARDING: Record<OnboardingState, OnboardingStepConfig> =
  Object.fromEntries(
    Object.entries({
      [OnboardingState.SignUp]: {
        name: OnboardingState.SignUp,
        steps: [],
        isModal: true,
      },
      [OnboardingState.WhatDescribesYou]: {
        name: OnboardingState.WhatDescribesYou,
        steps: [{}],
        isModal: true,
        isSurvey: true,
      },
      [OnboardingState.HowExperienced]: {
        name: OnboardingState.HowExperienced,
        steps: [{}],
        isModal: true,
        isSurvey: true,
      },
      [OnboardingState.HearAboutUs]: {
        name: OnboardingState.HearAboutUs,
        steps: [{}],
        isModal: true,
        isSurvey: true,
      },
      [OnboardingState.CreateFirst]: {
        name: OnboardingState.CreateFirst,
        steps: [{}],
        isModal: true,
        isSurvey: true,
      },
      [OnboardingState.UpgradePlan]: {
        name: OnboardingState.UpgradePlan,
        steps: [{}],
        isModal: true,
      },
      [OnboardingState.Survey]: {
        name: OnboardingState.Survey,
        steps: [{}],
      },
      [OnboardingState.ChooseTemplate]: {
        name: OnboardingState.ChooseTemplate,
        label: 'Choose a template',
        openMenu: 'template',
        steps: [
          {
            description:
              'Use free templates for private and commercial projects.',
            refs: [AnchorRefKeys.LEFT_MENU, AnchorRefKeys.TEMPLATES_BUTTON],
            displayIndex: 1,
          },
        ],
      },
      [OnboardingState.EditText]: {
        name: OnboardingState.EditText,
        label: 'Select & edit text',
        openMenu: 'close',
        steps: [
          {
            label: 'Add text',
            icon: <IconTextLayer color={themeStyle.colorText} />,
            description: 'Press "T" to add text.',
            dispatch: { event: ADD_TEXT, value: { deselect: true } },
            refs: [AnchorRefKeys.ARTBOARD],
            displayIndex: 2,
          },
          {
            description: 'Customise the template by adding your text.',
            dispatch: { event: MAIN_SELECT },
            refs: [AnchorRefKeys.ARTBOARD],
            displayIndex: 2,
          },
        ],
      },
      [OnboardingState.TransformText]: {
        name: OnboardingState.TransformText,
        label: 'Transform text',
        fallback: {
          label: 'Select text',
          description: 'Select text to add effects.',
          dispatch: { event: MAIN_SELECT },
          refs: [AnchorRefKeys.ARTBOARD],
          displayIndex: 2,
        },
        steps: [
          {
            description: 'Use transformation tools to warp your text.',
            dispatch: { event: DESELECT_OBJECTS },
            refs: [AnchorRefKeys.STYLES_MENU, AnchorRefKeys.TRANSFORMATION],
            displayIndex: 3,
          },
        ],
      },
      [OnboardingState.AddElement]: {
        name: OnboardingState.AddElement,
        label: 'Choose an element',
        openMenu: 'elements',
        steps: [
          {
            description:
              'Drag and drop high-quality illustrations and customize them to your needs.',
            dispatch: { event: DESELECT_OBJECTS, value: { colors: true } },
            refs: [AnchorRefKeys.LEFT_MENU, AnchorRefKeys.ELEMENTS_BUTTON],
            displayIndex: 4,
          },
        ],
      },
      [OnboardingState.ColorPalette]: {
        name: OnboardingState.ColorPalette,
        label: 'Choose a color palette',
        openRightMenu: 'projectColors',
        steps: [
          {
            description:
              'Choose one of the trending color palettes or replace color by color.',
            refs: [AnchorRefKeys.COLORS_PANEL, AnchorRefKeys.COLORS_BUTTON],
            displayIndex: 5,
          },
        ],
      },
      [OnboardingState.Download]: {
        name: OnboardingState.Download,
        label: 'Download',
        steps: [
          {
            description:
              'Your designs will be saved automatically but you can download them in preferred format.',
            refs: [AnchorRefKeys.DOWNLOAD_PANEL, AnchorRefKeys.DOWNLOAD_BUTTON],
            displayIndex: 6,
            skipLabel: null,
            nextLabel: 'Finish',
          },
        ],
      },
      // last step must have only one non-nullish substep to display congrats modal
      [OnboardingState.Complete]: {
        name: OnboardingState.Complete,
        steps: [{}],
      },
      [OnboardingState.Skipped]: {
        name: OnboardingState.Skipped,
        steps: [],
        isSkip: true,
      },
    }).map(([key, obj], index: number) => {
      (obj as OnboardingStepConfig).index = index;
      return [key, obj];
    })
  ) as Record<OnboardingState, OnboardingStepConfig>;

export const VIDEO_ONBOARDING: Record<
  OnboardingVideoState,
  OnboardingStepConfig
> = {
  [OnboardingVideoState.Start]: {
    name: OnboardingVideoState.Start,
    index: 1,
    steps: [],
  },
  [OnboardingVideoState.Show]: {
    name: OnboardingVideoState.Show,
    index: 2,
    steps: [{}],
  },
};

export const ONBOARDING_FLOW = [OnboardingKeys.base];

const isKeyAfterInFlow = (
  targetKey: OnboardingKeys | null,
  key: OnboardingKeys
): boolean => {
  if (!targetKey) {
    return false;
  }
  const targetIndex = ONBOARDING_FLOW.indexOf(targetKey);
  const index = ONBOARDING_FLOW.indexOf(key);
  return targetIndex !== -1 && index > targetIndex;
};

const initBaseOnboarding = ({
  step,
  onboardingKey,
}: SurveyInitParams): Partial<OnboardingStoreState> => {
  const skipped = { skipped: [OnboardingKeys.base] };
  if (!step) {
    return skipped;
  }

  const steps: Record<OnboardingState, OnboardingStepConfig> =
    ONBOARDINGS[OnboardingKeys.base].data;

  if (isKeyAfterInFlow(onboardingKey, OnboardingKeys.base)) {
    updateBaseOnboarding(BASE_ONBOARDING.SKIPPED);
    return skipped;
  }

  const knownStep = steps[step.name as OnboardingState];
  // ignore unknown onboarding states
  if (!isValidUserStep(knownStep)) {
    return skipped;
  }

  return {
    onboardingKey: OnboardingKeys.base,
    step: steps[step.name as OnboardingState],
  };
};

const updateBaseOnboarding = async (
  step: OnboardingStepConfig,
  subStep = 0,
  next = false
): Promise<void> => {
  analytics.track(ONBOARDING_STEP, {
    label: `${next ? 'next-' : ''}${step.name}-${subStep}`,
  });

  await updateOnboardingState(step);

  sessionStorage.removeItem(ONBOARDING_USER_SURVERY_KEY); // remove previously used local onboarding state
};

export const ONBOARDINGS = {
  [OnboardingKeys.base]: {
    data: BASE_ONBOARDING,
    stepsCount: calcStepsCount(Object.values(BASE_ONBOARDING)),
    init: initBaseOnboarding,
    update: updateBaseOnboarding,
    skip: async (): Promise<void> => {
      await updateBaseOnboarding(BASE_ONBOARDING.SKIPPED);
    },
    reset: async (
      step: OnboardingStepConfig = BASE_ONBOARDING.SURVEY
    ): Promise<void> => {
      await updateBaseOnboarding(step);
    },
  },
  [OnboardingKeys.video]: {
    data: VIDEO_ONBOARDING,
    stepsCount: null,
    init: ({
      step,
      onboardingKey,
    }: SurveyInitParams): Partial<OnboardingStoreState> => {
      const skipped = { skipped: [OnboardingKeys.video] };
      const applied = {
        onboardingKey: OnboardingKeys.video,
        step: VIDEO_ONBOARDING[OnboardingVideoState.Start],
      };

      if (onboardingKey === OnboardingKeys.base) {
        return applied;
      } else if (onboardingKey === OnboardingKeys.video) {
        return { step: VIDEO_ONBOARDING.VIDEO_START };
      } else if (isKeyAfterInFlow(onboardingKey, OnboardingKeys.base)) {
        return skipped;
      }

      if (localStorage.getItem(SHOW_ONBOARDING_VIDEO_POPUP_LOCAL_STORAGE_KEY)) {
        return skipped;
      }

      // If the onboarding starts from the Survey => let's first show the video modal
      if (step?.name === OnboardingState.Survey) {
        return applied;
      }

      return skipped;
    },
    update: (step: OnboardingStepConfig): void => {
      if (step.name === OnboardingVideoState.Show) {
        localStorage.setItem(
          SHOW_ONBOARDING_VIDEO_POPUP_LOCAL_STORAGE_KEY,
          'shown'
        );
      }
    },
    skip: noop,
    reset: (): void => {
      localStorage.removeItem(SHOW_ONBOARDING_VIDEO_POPUP_LOCAL_STORAGE_KEY);
    },
  },
};
